import { computed, watch, reactive, ref } from "vue";

import { env, apiHostname } from "config";

import { cache, initCache, clearCache } from "helpers/_cache";
import { getDataWithoutLimit } from "helpers/_api";
import trans from "helpers/_translation";

import { portalStore, unauthorized } from "store/portal";

// NOTE: Department can be simply the company, or companies related to the user
// NOTE: But it can also be a combination, for example [Company - Item]
// NOTE: This to make the portal flexibel in use and support multiple scenario's

const cacheableValues = {
	active_uuid: undefined
};

const departmentDefaults = {
	...cacheableValues
};
export const departmentStore = reactive({
	loading: true,
	data: [],
	companies: [],
	related: {},
	...departmentDefaults
});
Object.keys(cacheableValues).forEach((key) => {
	initCache("department", key, departmentStore);
});

export const department_append = computed(() => {
	return env("department_append");
});
export const department_scope = computed(() => {
	if (!department_append.value || !department_append.value.length) {
		return ["company"];
	}
	return [
		"company",
		...department_append.value
	];
});
export const departments = computed(() => {
	return departmentStore.data;
	// return department_scope.value.filter(key => {
	// 	console.log("a[key]",a[key],a,key);
	// 	return Array.isArray(a[key]) ? !!a[key].length : !!a[key];
	// });
});

export const isDepartmentActive = (department) => {
	return computed(() => {
		return department_id.value === department_uuid(department);
	}).value;
};

export const department_uuid = (target) => {
	return computed(() => {
		const uuid = [];
		department_scope.value.forEach(key => {
			if (target?.[key]?.data?.id) {
				uuid.push(target[key].data.id);
			}
		});
		return uuid.join("_");
	}).value;
};
export const department_title = (department) => {
	return computed(() => {
		if (unauthorized.value && !department_append.value) {
			return trans("Onbekend");
		}

		// Check if there are multiple of any related items
		const hasMultiple = department_append.value?.some(key => departmentStore.related[key]?.[department.company.data.id]?.length > 1) ?? false;

		const title = [];
		department_scope.value.forEach(key => {
			// In case of single item, hide company from title
			if (!hasMultiple && (key === "company")) {
				return;
			}
			if (department[key]?.data?.title) {
				title.push(department[key].data.title);
			}
		});
		return title.join(" - ") || department.company.data.title;
	}).value;
};

export const department = computed(() => departments.value.find((item) => {
	return department_uuid(item) === departmentStore.active_uuid;
}));
export const department_id = computed(() => {
	return department_uuid(department.value);
});

export const setActiveByUuid = (uuid) => {
	departmentStore.active_uuid = uuid;
};
export const setActive = (department) => {
	departmentStore.active_uuid = department_uuid(department);
};


const getDepartmentByKeyAndCompany = (key, company) => {
	if (departmentStore.related?.[key]?.[company.data.id]) {
		return;
	}

	// Set default values
	if (!(key in departmentStore.related)) {
		departmentStore.related[key] = {};
	}
	if (!(company.data.id in departmentStore.related[key])) {
		departmentStore.related[key][company.data.id] = [];
	}
	// Build our filters
	const filters = JSON.parse(JSON.stringify(env(`variables.${key}.filters`)));
	filters.forEach(filter => {
		if (!filter.length) return;
		["%company.id%"].forEach((replace) => {
			filter[1].v.forEach((item, index) => {
				filter[1].v[index] = item.replace(replace, company.data.id);
			});
		});
	});
	filters.push([
		"FieldFilter",
		{
			"c": "workflowcardtype_id",
			"v": env(`variables.${key}.workflowcardtype_id`)
		}
	],
	[
		"FieldFilter",
		{
			"column": "state",
			"value": "Online"
		}
	]);

	// Get our data
	const tmpData = {
		data: []
	};
	getDataWithoutLimit(`${apiHostname}/api/v1.0/workflowcard`, filters, tmpData, "data", false).then((result) => {
		departmentStore.related[key][company.data.id] = tmpData.data;
	});
};


const generateCombinations = (base, relatedKeys, relatedData, index = 0) => {
	if (!relatedKeys || (index >= relatedKeys.length)) return [base];

	const key = relatedKeys[index];
	const items = relatedData[key] ?? [null]; // null to still create the combo even if no data

	const combinations = [];

	items.forEach(item => {
		const nextBase = { ...base, [key]: item };
		const nestedCombos = generateCombinations(nextBase, relatedKeys, relatedData, index + 1);
		combinations.push(...nestedCombos);
	});

	return combinations;
};

watch(
	() => [
		departmentStore.related,
		departmentStore.companies
	],
	() => {
		// Loop through scope
		const combinations = [];

		if (department_append.value?.length && !Object.keys(departmentStore.related)) {
			return;
		}

		// Based on the configured department append, append the data by module
		departmentStore.companies.forEach(company => {
			// Store the ID for reference
			const companyId = company.data.id;

			if (department_append.value && !department_append.value.length) {
				combinations.push({
					company: company
				});
				return;
			}

			const relatedData = {};
			// Build relatedData map
			department_append.value && department_append.value.forEach(key => {
				relatedData[key] = departmentStore.related?.[key]?.[companyId];
			});
			// Generate all combinations for this company
			combinations.push(...generateCombinations({ company }, department_append.value, relatedData));
		});
		departmentStore.data = combinations;
	},
	{
		immediate: true,
		deep: true
	}
);

export const initDepartments = async () => {
	watch(
		() => [
			department_append,
			portalStore.company,
			portalStore.companies
		],
		() => {

			const companies = portalStore.company?.length ? portalStore.company : portalStore.companies;
			if (!companies) return;

			// Based on the configured department append, append the data by module
			companies.forEach(company => {
				department_append.value && department_append.value.forEach(key => {
					getDepartmentByKeyAndCompany(key, company);
				});
			});

			departmentStore.companies = companies;

		},
		{
			immediate: true,
			deep: true
		}
	);
};

// Watchers
// -- Based on value change store active
watch(
	() => departments.value,
	(items) => {
		// If currently no active, set the first one
		if (!departmentStore.active_uuid && items?.length) {
			departmentStore.active_uuid = department_uuid(items[0]);
		}
	},
	{
		immediate: true
	}
);